import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, TextField, Fade, CircularProgress, keyframes } from '@mui/material';
import axiosClient from '../../services/axiosClient';
import { format } from 'date-fns';
import PrimaryButton from '../PrimaryButton';
import CustomDatePicker from '../CustomDatePicker';

// Animation for loading state
const waveAnimation = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(5deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
`;

// Types for questions and answers
interface Question {
  id: string;
  text: string;
  type: 'text' | 'single-choice' | 'long-text' | 'date';
  options?: string[];
  required?: boolean;
  conditional?: (answers: Record<string, any>) => boolean;
  placeholder?: string;
  subtext?: string;
}

interface Section {
  id: string;
  title: string;
  questions: Question[];
}

interface QuestionnaireEngineProps {
  minimum_monthly_income?: number;
  onComplete?: (answers: Record<string, any>) => void;
  localStorageKey?: string;
  existingProspect?: any;
}

// Function to create the questionnaire data structure
const createQuestionnaireData = (minimum_monthly_income?: number, existingProspect?: any): Section[] => {
  // If existing prospect, only show minimum monthly income question
  if (existingProspect) {
    return [
      {
        id: 'income-verification',
        title: 'Income Verification',
        questions: [
          {
            id: 'makes_enough_income',
            text: `Do you make at least $${minimum_monthly_income?.toLocaleString('en-US')} a month?`,
            type: 'single-choice',
            options: ['Yes', 'No'],
            required: true,
          },
        ],
      },
    ];
  }

  // Full questionnaire for new prospects
  return [
    {
      id: 'pets',
      title: 'Pets',
      questions: [
        {
          id: 'has_pets',
          text: 'Do you have any pets?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
        },
        {
          id: 'num_dogs',
          text: 'How many dogs do you have?',
          type: 'single-choice',
          options: ['0', '1', '2', '3+'],
          required: true,
          conditional: (answers: Record<string, any>) => answers['has_pets'] === 'Yes',
        },
        {
          id: 'num_cats',
          text: 'How many cats do you have?',
          type: 'single-choice',
          options: ['0', '1', '2', '3+'],
          required: true,
          conditional: (answers: Record<string, any>) => answers['has_pets'] === 'Yes',
        },
      ],
    },
    {
      id: 'rental-history',
      title: 'Rental history',
      questions: [
        {
          id: 'new_renter',
          text: 'Is this your first time renting?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
        },
        {
          id: 'has_year_long_rental',
          text: 'Have you rented any place for a year or longer?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
          conditional: (answers: Record<string, any>) => answers['new_renter'] === 'No',
        },
        {
          id: 'current_living_situation',
          text: 'What is your current living situation?',
          type: 'single-choice',
          options: [
            'Renting',
            'Homeowner',
            'Staying with family or friends',
            'School/university housing',
            'Short-term housing (e.g., Airbnb, hotel)',
            'Transitional housing/shelter',
            'Other',
          ],
          required: true,
        },
        {
          id: 'living_situation_explained',
          text: 'Please explain your living situation?',
          type: 'text',
          required: true,
          conditional: (answers: Record<string, any>) => answers['current_living_situation'] === 'Other',
        },
      ],
    },
    {
      id: 'work-study-finances',
      title: 'Work or Study',
      questions: [
        {
          id: 'student',
          text: 'Are you a student?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
        },
        {
          id: 'undergraduate',
          text: 'Are you an undergraduate?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
          conditional: (answers: Record<string, any>) => answers['student'] === 'Yes',
        },
        {
          id: 'currently_employed',
          text: 'Are you currently employed?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
        },
        {
          id: 'employer',
          text: 'Who is your employer?',
          type: 'text',
          required: true,
          placeholder: 'Company name or organization',
          conditional: (answers: Record<string, any>) => answers['currently_employed'] === 'Yes',
        },
        {
          id: 'occupation',
          text: 'What is your job title?',
          type: 'text',
          required: true,
          placeholder: 'Job title or role',
          subtext: 'Sharing this instantly makes you desirable to landlords.',
          conditional: (answers: Record<string, any>) => answers['currently_employed'] === 'Yes',
        },
        {
          id: 'rent_payment_without_job',
          text: 'How would you pay for rent?',
          type: 'single-choice',
          options: ['Grants or Loans', 'Cosigner(s)', 'Combo of Grants Loans, Cosigners', 'Something else'],
          required: true,
          conditional: (answers: Record<string, any>) => answers['currently_employed'] === 'No',
        },
        {
          id: 'explain_rent_payment',
          text: 'Please explain how you would pay rent?',
          type: 'text',
          required: true,
          conditional: (answers: Record<string, any>) => answers['currently_employed'] === 'No' && answers['rent_payment_without_job'] === 'Something else',
        },
        {
          id: 'income_verifiable',
          text: 'Is your income verifiable by paystubs or bank statements?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
          conditional: (answers: Record<string, any>) => answers['currently_employed'] === 'Yes',
        },
        {
          id: 'makes_enough_income',
          text: `Do you make at least $${minimum_monthly_income?.toLocaleString('en-US')} a month?`,
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
          conditional: (answers: Record<string, any>) => answers['currently_employed'] === 'Yes',
        },
        {
          id: 'has_current_job_for_year',
          text: 'Have you been at your current job for at least one year?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
          conditional: (answers: Record<string, any>) => answers['currently_employed'] === 'Yes',
        },
        {
          id: 'employment_history_last_3_years',
          text: 'In the last 3 years, have you worked at any job for at least one year?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
          conditional: (answers: Record<string, any>) => answers['currently_employed'] === 'Yes' && answers['has_current_job_for_year'] === 'No',
        },
        {
          id: 'will_do_background_and_credit_check',
          text: 'Are you willing to undergo a credit & background check?',
          type: 'single-choice',
          options: ['Yes', 'No'],
          required: true,
        },
      ],
    },
    {
      id: 'who-are-you',
      title: 'Last, Basic info',
      questions: [
        { id: 'name', text: 'What is your name?', type: 'text', required: true },
        {
          id: 'age_range',
          text: 'What is your age range?',
          type: 'single-choice',
          options: ['Under 18', '18–24', '25–34', '35–44', '45+', 'Prefer not to say'],
          required: true,
        },
        {
          id: 'number_of_applicants',
          text: 'How many are applying?',
          type: 'single-choice',
          options: ['Just me', '2 people', '3 people', '4+ people'],
          required: true,
        },
        { id: 'move_in_date_requested', text: 'What move in date are you seeking?', type: 'date', required: true },
        {
          id: 'lease_length_requested',
          text: 'What lease length are you looking for?',
          type: 'single-choice',
          options: ['12 month', '6 month', '3 month', 'Month to month'],
          required: true,
        },
        { id: 'phone_number', text: 'What is your phone number?', type: 'text', required: true },
        { id: 'moving_reason', text: 'Why are you moving?', type: 'text', required: true },
        {
          id: 'short_intro',
          text: 'Tell us a bit about yourself',
          type: 'long-text',
          required: true,
          placeholder: "Hi there! I am a 25 year old nurse working at Hopkins. I will be moving with my dog Charles, he's a 2 year old Golden Doodle. I love running, hiking and cooking.",
          subtext: 'A warm intro is a great way to break the ice with landlords, it goes a long way.',
        },
      ],
    },
    {
      id: 'tour-preference',
      title: 'Tour Preference',
      questions: [
        { id: 'tour_preference', text: 'How would you like to take the tour?', type: 'single-choice', options: ['In person', 'Virtual'], required: true },
      ],
    },
  ];
};

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone: string): boolean {
  // Simple US phone validation (customize as needed)
  return /^\d{10}$/.test(phone.replace(/\D/g, ''));
}

const QuestionnaireEngine: React.FC<QuestionnaireEngineProps> = ({ minimum_monthly_income, onComplete, localStorageKey = 'prospect_questionnaire_answers', existingProspect }) => {
  // State
  const [sectionIdx, setSectionIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [inputValue, setInputValue] = useState('');
  const [showQuestion, setShowQuestion] = useState(true);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [fieldTouched, setFieldTouched] = useState(false);
  const [isGeneratingIntro, setIsGeneratingIntro] = useState(false);
  const [introGenerated, setIntroGenerated] = useState(false);

  // Questionnaire structure memoized
  const questionnaire = useMemo(() => createQuestionnaireData(minimum_monthly_income, existingProspect), [minimum_monthly_income, existingProspect]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(localStorageKey);
    if (saved) {
      setAnswers(JSON.parse(saved));
    }
  }, [localStorageKey]);

  // Save to localStorage on answers change
  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(answers));
  }, [answers, localStorageKey]);

  // Get current section/question
  const section = questionnaire[sectionIdx];
  // Only show questions whose conditionals (if any) are met
  const visibleQuestions = section.questions.filter(
    q => !q.conditional || q.conditional(answers)
  );

  // Ensure questionIdx is valid for the current visibleQuestions array
  useEffect(() => {
    if (questionIdx >= visibleQuestions.length) {
      setQuestionIdx(0);
    }
  }, [visibleQuestions.length, questionIdx]);

  const question = visibleQuestions[questionIdx];

  // Pre-fill input when going back
  useEffect(() => {
    setInputValue(answers[question?.id] ?? '');
    setFieldError(null); // Reset error on question change
    setFieldTouched(false); // Reset touched state
  }, [questionIdx, sectionIdx, question]);

  // Guard against undefined question
  if (!question) {
    return null;
  }

  // Validation helpers
  const isEmail = question.id === 'email';
  const isPhone = question.id === 'phone_number';

  const validateInput = (value: string) => {
    if (isEmail && value) {
      if (!isValidEmail(value)) {
        return 'Please enter a valid email address.';
      }
    }
    if (isPhone && value) {
      if (!isValidPhone(value)) {
        return 'Please enter a valid phone number.';
      }
    }
    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);
    // Only show error if field has been touched
    if (fieldTouched) {
      setFieldError(validateInput(value));
    } else {
      setFieldError(null);
    }
  };

  const handleInputBlur = () => {
    setFieldTouched(true);
    setFieldError(validateInput(inputValue));
  };

  // Generate AI intro
  const generateIntro = async () => {
    setIsGeneratingIntro(true);
    try {
      const requestData = {
        moveInDate: answers['move_in_date_requested'] || '',
        leaseLength: answers['lease_length_requested'] || '',
        employer: answers['employer'] || '',
        occupation: answers['occupation'] || '',
        numberOfApplicants: answers['number_of_applicants'] || '',
        numDogs: answers['num_dogs'] || '0',
        numCats: answers['num_cats'] || '0',
        movingReason: answers['moving_reason'] || '',
      };

      const response = await axiosClient.post('/api/v1/open_ai_api/generate_renter_intro', requestData);
      const data = response.data;
      setInputValue(data.intro);
      setIntroGenerated(true);
    } catch (error) {
      console.error('Error generating intro:', error);
      // Fallback to basic intro if API fails
      const employer = answers['employer'] || 'my company';
      const occupation = answers['occupation'] || 'professional';
      const numDogs = answers['num_dogs'] || '0';
      const numCats = answers['num_cats'] || '0';
      const movingReason = answers['moving_reason'] || '';
      
      let petInfo = '';
      if (numDogs !== '0' || numCats !== '0') {
        const pets = [];
        if (numDogs !== '0') pets.push(`${numDogs} dog${numDogs !== '1' ? 's' : ''}`);
        if (numCats !== '0') pets.push(`${numCats} cat${numCats !== '1' ? 's' : ''}`);
        petInfo = ` I have ${pets.join(' and ')}.`;
      }
      
      const fallbackIntro = `Hi! I'm a ${occupation} working at ${employer}.${petInfo}${movingReason ? ` I'm moving because ${movingReason.toLowerCase()}.` : ''} I'm looking forward to finding my next home!`;
      setInputValue(fallbackIntro);
      setIntroGenerated(true);
    } finally {
      setIsGeneratingIntro(false);
    }
  };

  // Validation for Next button
  const isValid =
    inputValue !== '' &&
    (!isEmail || isValidEmail(inputValue)) &&
    (!isPhone || isValidPhone(inputValue));

  // Handle answer
  const handleAnswer = (value: any) => {
    setShowQuestion(false);
    setTimeout(() => {
      // Save answer
      const newAnswers = { ...answers, [question.id]: value };

      // Clear subsequent answers in the current section
      const currentSectionQuestions = section.questions;
      const currentQuestionIndex = currentSectionQuestions.findIndex(q => q.id === question.id);
      currentSectionQuestions.slice(currentQuestionIndex + 1).forEach(q => {
        delete newAnswers[q.id];
      });

      setAnswers(newAnswers);
      setInputValue('');

      // Calculate next question index
      const newVisibleQuestions = section.questions.filter(
        q => !q.conditional || q.conditional(newAnswers)
      );
      const currentVisibleIndex = newVisibleQuestions.findIndex(q => q.id === question.id);

      // Go to next question or section
      if (currentVisibleIndex < newVisibleQuestions.length - 1) {
        setQuestionIdx(currentVisibleIndex + 1);
      } else if (sectionIdx < questionnaire.length - 1) {
        setSectionIdx(s => s + 1);
        setQuestionIdx(0);
      } else {
        // End of questionnaire (submit here)
        if (onComplete) onComplete(newAnswers);
      }
      setShowQuestion(true);
    }, 200); // fade duration
  };

  // Handle go back
  const handleGoBack = () => {
    setShowQuestion(false);
    setTimeout(() => {
      if (questionIdx > 0) {
        setQuestionIdx(q => q - 1);
      } else if (sectionIdx > 0) {
        const prevSection = questionnaire[sectionIdx - 1];
        const prevVisibleQuestions = prevSection.questions.filter(
          q => !q.conditional || q.conditional(answers)
        );
        setSectionIdx(s => s - 1);
        setQuestionIdx(prevVisibleQuestions.length - 1);
      }
      setShowQuestion(true);
    }, 200);
  };

  // Progress bar (section only)
  return (
    <Box sx={{ mt: 6, width: '100%', maxWidth: 500, mx: 'auto' }}>
      <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
        {section.title} - Section {sectionIdx + 1} of {questionnaire.length}
      </Typography>
      <Fade in={showQuestion} timeout={200}>
        <Box sx={{ width: { xs: '90%', sm: '100%' }, mx: 'auto', pt: '5vh', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="h6" sx={{ mb: question.subtext ? 1 : 2, fontWeight: 400, textAlign: 'left' }}>
            {question.text}
          </Typography>
          {question.subtext && (
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
              {question.subtext}
            </Typography>
          )}
          {question.type === 'date' ? (
            <Box sx={{ mb: 2, width: '100%' }}>
              <CustomDatePicker
                value={inputValue ? new Date(inputValue + 'T12:00:00') : null}
                onChange={(newValue) => {
                  const formatted = newValue ? format(newValue, 'yyyy-MM-dd') : '';
                  setInputValue(formatted);
                }}
                minDate={new Date()}
              />
            </Box>
          ) : question.type === 'text' ? (
            <TextField
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              autoFocus
              size="small"
              variant="outlined"
              placeholder={question.placeholder}
              sx={{ mb: 2, width: '100%' }}
              error={!!fieldError}
              helperText={fieldError}
            />
          ) : question.type === 'long-text' ? (
            <Box sx={{ width: '100%', mb: 2 }}>
              {isGeneratingIntro ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Box
                    sx={{
                      display: 'inline-block',
                      animation: `${waveAnimation} 2s ease-in-out infinite`,
                      fontSize: '3rem',
                      mb: 2,
                    }}
                  >
                    🏠
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Crafting your perfect intro...
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Our AI is writing a personalized introduction for you.
                  </Typography>
                  <CircularProgress size={24} sx={{ mt: 2 }} />
                </Box>
              ) : (
                <>
                  <TextField
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    autoFocus
                    multiline
                    rows={6}
                    variant="outlined"
                    placeholder={question.placeholder}
                    sx={{ width: '100%' }}
                    error={!!fieldError}
                    helperText={fieldError}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={generateIntro}
                      disabled={isGeneratingIntro}
                      sx={{
                        borderColor: 'grey.400',
                        color: 'grey.700',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: 'grey.500',
                          bgcolor: 'grey.100'
                        }
                      }}
                    >
                      {introGenerated ? 'Regenerate with AI' : 'Generate with AI'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          ) : question.type === 'single-choice' && question.options ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mb: 2, width: '100%' }}>
              {question.options.map(option => (
                <Button
                  key={option}
                  variant={inputValue === option ? 'contained' : 'outlined'}
                  onClick={() => handleAnswer(option)}
                  sx={{
                    width: 'auto',
                    margin: '0.5rem 0',
                    padding: '1rem',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    backgroundColor: inputValue === option ? '#F5F5F5' : 'transparent',
                    color: '#000',
                    border: '2px solid #E5E5E5',
                    boxShadow: 'none',
                    borderColor: inputValue === option ? '#000' : '#E5E5E5',
                    '&:hover': {
                      borderColor: '#000',
                      backgroundColor: inputValue === option ? '#F5F5F5' : 'transparent',
                      color: '#000',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {option}
                </Button>
              ))}
            </Box>
          ) : null}
          {/* For text/date, show a submit button only if valid */}
          {(question.type === 'text' || question.type === 'date' || question.type === 'long-text') && !isGeneratingIntro && (
            <PrimaryButton
              disabled={!isValid}
              onClick={() => handleAnswer(inputValue)}
              sx={{ mt: 1, mb: 2, alignSelf: 'flex-start', minWidth: 120 }}
            >
              Next
            </PrimaryButton>
          )}
          {/* Go back button (hyperlink style, bottom) */}
          {(sectionIdx > 0 || questionIdx > 0) && (
            <Button
              variant="text"
              size="small"
              onClick={handleGoBack}
              sx={{
                color: '#1976d2',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.95rem',
                mt: 6,
                alignSelf: 'flex-start',
                '&:hover': {
                  textDecoration: 'underline',
                  background: 'none',
                },
              }}
            >
              Go back to previous question
            </Button>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

export default QuestionnaireEngine;